import { clsx } from 'clsx';
import { Col, Popconfirm, Row } from 'antd';
import { Button } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import {
  DeleteButton,
  EditProps,
  required,
  SaveButton,
  SimpleForm,
  TopToolbar,
  useNotify,
  useRefresh,
} from 'react-admin';

import {
  AntdDateInput,
  Container,
  Forbidden,
  FormRow,
  NumberInput,
  PasswordInput,
  PhoneMaskedInput,
  SelectInput,
  SwitchInput,
  TextInput,
} from '@components';
import { ManagerLoginBtn } from './ManagerLoginBtn';
import { useRbacEdit, useRbacView, useRecord } from '@hooks';
import { PASSWORD_MIN_LENGTH } from '@pages/manager';
import { ManagerAvatar } from './ManagerAvatar/manager-avatar';
import { IndividualManagers } from './IndividualManagers';
import {
  Dealer,
  DealerType,
  genderOptions,
  Manager,
  typeOfManager,
  typeOfMembership,
} from '@types';
import { AddFarmer } from './AddFarmer';
import cls from './DealerAccount.module.css';
import { axiosErrorToString, dateFormatter } from '@utils';
import { dealerHttp } from '@network';
import { CreditDocuments } from '@pages/dealer/DealerAccount/CreditDocuments';

export const DealerAccount = (props: any | EditProps) => {
  const dealer = useRecord<Dealer>(props);
  const rbacView = useRbacView();
  const rbacEdit = useRbacEdit();
  const notify = useNotify();
  const refresh = useRefresh();
  const [password, setPassword] = useState<string>('');

  if (!dealer) return null;

  const manager = dealer.manager as Manager;
  const isBuyer = dealer.type === DealerType.BUYER;
  const isRetailer = dealer.type === DealerType.RETAILER;
  const isAssociate = dealer.type === DealerType.ASSOCIATE;

  const mainColGrid = {
    xxl: { title: 7, children: 17 },
    sm: { title: 10, children: 14 },
    xs: { title: 24, children: 24 },
  };
  const passwordGrid = {
    xs: { title: 24, children: 24 },
    xxl: { title: 8, children: 16 },
  };

  if (!rbacView) {
    return <Forbidden />;
  }

  const copyPassword = (isUpdate: boolean) => {
    if (password.length < PASSWORD_MIN_LENGTH) {
      return;
    }

    const message = `${isUpdate ? 'Manager is updated. ' : ''}Password is copied to clipboard.`;
    navigator.clipboard.writeText(password);

    setTimeout(() => {
      notify(message);
    }, 500);
  };

  const onDeleteAccount = async () => {
    if (!dealer?.id) return;

    try {
      await dealerHttp.deleteAccount(Number(dealer.id));
      notify('Account was deleted', { type: 'success' });
      refresh();
    } catch (e) {
      notify(axiosErrorToString(e), { type: 'error' });
    }
  };

  const showDeleteAccountAction = dealer && !!dealer.manager;

  const Actions = () => {
    return (
      <TopToolbar className={cls.actions}>
        <DeleteButton className={clsx(cls.action, cls.actionDelete)} />

        {showDeleteAccountAction && (
          <Popconfirm title="Delete Account?" onConfirm={onDeleteAccount}>
            <Button className={clsx(cls.action, cls.actionDeleteAccount)}>
              Delete Account
            </Button>
          </Popconfirm>
        )}

        <SaveButton
          label="Save"
          type="button"
          className={clsx(cls.action, cls.actionSave)}
          variant="outlined"
          mutationOptions={{
            onSuccess: () => {
              if (password) {
                copyPassword(true);
              } else {
                notify('Manager is updated.');
              }
            },
          }}
        />
      </TopToolbar>
    );
  };

  const maxLength = 50;
  const validate = (formValues: any) => {
    const errors: any = {};
    const values = formValues['manager'] || {};

    ['name', 'email', 'gender', 'phone', 'password', 'position', 'confirmPassword', 'membership', 'secretCode']
      .forEach(key => {
        const formKey = `manager.${key}`;
        if (values[key]?.length > maxLength) {
          errors[formKey] = `Max length is ${maxLength} characters`;
        }
        if (key === 'password' || key === 'confirmPassword') {
          if (key === 'password') {
            if (values[key]?.length && values[key]?.length < PASSWORD_MIN_LENGTH) {
              errors[formKey] = `Password must be longer than or equal to ${PASSWORD_MIN_LENGTH} characters`;
            }
          }
          if (key === 'confirmPassword') {
            if (values.password && (!values[key] || values.password !== values[key])) {
              errors[formKey] = 'Passwords must match';
            }
          }
        } else if (!values[key]) {
          if (key === 'phone') {
            errors[formKey] = 'Phone number is mandatory';
          } else if (key === 'position') {
            errors[formKey] = 'Position is mandatory';
          } else if (key === 'buyerId' && isAssociate) {
            // no buyerId is required for Solo-Associate manager
          } else if (key === 'membership' && !isRetailer) {
            // no membership is required for manager buyer/associate
          } else if (key === 'secretCode') {
            // no required field
          } else {
            errors[formKey] = 'This field is required';
          }
        }
        // check phone
        if (key === 'phone' && !errors[key]) {
          const phoneLength = (values[key] || '').replace(/\D/g, '').length;
          if (phoneLength < 12 || phoneLength > 13) {
            errors[formKey] = 'Phone number is not correct';
          }
        }
        // check email
        if (key === 'email' && !errors[key]) {
          const EMAIL_REGEXP = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/iu;
          if (!EMAIL_REGEXP.test(values[key] || '')) {
            errors[formKey] = 'Email is not correct';
          }
        }
        if (key === 'secretCode') {
          if (values[key]) {
            if (values[key].length !== 6) {
              errors[formKey] = 'Code must be equal to 6 characters';
            }
          } else {
            delete errors[formKey];
          }
        }
      });

    return errors;
  };

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
  };

  const type = isBuyer ? 'Off Taker'
    : isRetailer ? 'Retail Store' : 'Associate';
  const companyTitle = isBuyer ? 'Buyer'
    : isRetailer ? 'Retailer' : 'Associate';
  const companyName = isBuyer ? 'Company name'
    : isRetailer ? 'Retail store' : 'Associate name';

  return (
    <SimpleForm
      toolbar={rbacEdit ? <Actions /> : false}
      className={clsx(cls.form, 'manager-form')}
      validate={validate}
      mode="onChange"
    >
      <Container max className={cls.container}>
        <Row gutter={0} className={cls.wrapper} align="stretch">
          <Col xs={24} lg={14} className={cls.wrapperCol}>
            <div className={cls.block}>
              <div className={cls.blockMiddle}>
                <div className={cls.blockMiddleIcon}>
                  <ManagerAvatar />
                  {!manager?.id ? null : (
                    <AddFarmer manager={manager} />
                  )}
                  {isBuyer || isRetailer ? (
                    <CreditDocuments
                      dealer={dealer}
                      editable={rbacEdit}
                      refresh={refresh}
                    />
                  ) : null}
                </div>
                <div className={cls.blockMiddleInfo}>
                  <div className={cls.blockHeader}>Manager</div>
                  <FormRow
                    title="Name*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <TextInput
                      name="manager.name"
                      source="manager.name"
                      required autoFocus
                      label={false}
                      inputProps={{ maxLength }}
                      onInput={transformTrimInput}
                    />
                  </FormRow>
                  <FormRow
                    title="Phone*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <PhoneMaskedInput
                      name="manager.phone"
                      source="manager.phone"
                      className={cls.phone}
                    />
                  </FormRow>
                  <FormRow
                    title="Email*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <TextInput
                      name="manager.email"
                      source="manager.email"
                      required
                      label={false}
                      type="email"
                      inputProps={{ maxLength }}
                      onInput={transformTrimInput}
                    />
                  </FormRow>
                  <FormRow
                    title="Gender*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <SelectInput
                      label=""
                      name="manager.gender"
                      source="manager.gender"
                      choices={genderOptions}
                    />
                  </FormRow>
                  {isBuyer ? <FormRow
                    title="Type of Manager:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <SelectInput
                      label=""
                      name="manager.typeOfManager"
                      source="manager.typeOfManager"
                      choices={typeOfManager}
                    />
                  </FormRow> : <div className={cls.type}>Type of Manager: {type}</div>}
                  <FormRow
                    title="VP:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <NumberInput
                      name="manager.vp"
                      source="manager.vp"
                      label={false}
                      onInput={transformNumberInput}
                    />
                  </FormRow>
                  {isRetailer ? <FormRow
                    title="Membership*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <SelectInput
                      label=""
                      name="manager.membership"
                      source="manager.membership"
                      choices={typeOfMembership}
                      validate={required()}
                    />
                  </FormRow> : null}
                  <FormRow
                    title="Licensed through:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <AntdDateInput
                      name="manager.licensedTrough"
                      source="manager.licensedTrough"
                      label=""
                    />
                  </FormRow>
                </div>
                <div className={cls.checkboxRow}>
                  <SwitchInput
                    name="manager.isEnabled"
                    source="manager.isEnabled"
                    label="Is enabled"
                    defaultValue={true}
                    className={cls.switcherField}
                  />
                  <SwitchInput
                    disabled={isAssociate}
                    name="manager.isSuperadmin"
                    source="manager.isSuperadmin"
                    label="Supervisor"
                    defaultValue={false}
                    className={cls.switcherField}
                  />
                </div>
              </div>
            </div>
          </Col>

          {/* RIGHT COLUMN: Buyer + Password */}
          <Col xs={24} lg={10} className={cls.wrapperCol}>
            <div className={cls.block}>
              <div className={cls.blockMiddle}>
                <div className={cls.blockHeader}>{companyTitle}</div>
                <div className={cls.formRows}>
                  <FormRow
                    title={`${companyName}*:`}
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <TextInput
                      name={'name'}
                      source={'name'}
                      required
                      label={false}
                      inputProps={{ maxLength }}
                      onBeforeInputCapture={e => e.preventDefault()}
                    />
                  </FormRow>
                  <FormRow
                    title="Position*:"
                    width={mainColGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.formLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <TextInput
                      name="manager.position"
                      source="manager.position"
                      label={false}
                      inputProps={{ maxLength }}
                      onInput={transformTrimInput}
                    />
                  </FormRow>
                </div>
              </div>
            </div>
            <div className={clsx(cls.block, cls.blockPassword)}>
              <div className={cls.blockHeader}>
                <div>Password</div>
                <Button
                  className={cls.passwordButton}
                  size="small"
                  onClick={() => copyPassword(false)}
                  disabled={password.length < PASSWORD_MIN_LENGTH}
                  type="button"
                >Copy password</Button>
              </div>
              <div className={cls.mainPasswordInfo}>
                <div className={cls.mainPasswordCol}>
                  <FormRow
                    title="Password:"
                    width={passwordGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.passwordLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <PasswordInput
                      name="manager.password"
                      source="manager.password"
                      label={false}
                      onChange={onPasswordChange}
                      inputProps={{ maxLength }}
                    />
                  </FormRow>
                  <FormRow
                    title="Confirm Password:"
                    width={passwordGrid}
                    rowClassName={clsx(cls.formRow, cls.mainFormRow)}
                    colTitleClassName={cls.passwordLabelCol}
                    colChildrenClassName={clsx(cls.formValueCol, cls.formMainValue)}
                  >
                    <PasswordInput
                      name="manager.confirmPassword"
                      source="manager.confirmPassword"
                      label={false}
                      inputProps={{ maxLength }}
                    />
                  </FormRow>
                </div>
                <div className={cls.mainLoginCol}>
                  <ManagerLoginBtn source="login" bg="#042E6B" text="Login" />
                </div>
              </div>
            </div>
            <div className={cls.block}>
              <div className={cls.infoRow}>
                <div className={cls.infoCol}>
                  <div className={cls.infoData}>
                    <div className={cls.infoLabel}>Added at:</div>
                    <div className={cls.infoValue}>
                      {dateFormatter.toDateSlash(manager?.createdAt || undefined)}
                    </div>
                  </div>
                  <div className={cls.infoData}>
                    <div className={cls.infoLabel}>Registered at:</div>
                    <div className={cls.infoValue}>
                      {dateFormatter.toDateSlash(manager?.registeredAt || undefined)}
                    </div>
                  </div>
                </div>
                <div className={cls.infoCol}>
                  <div className={cls.infoData}>
                    <div className={cls.infoLabel}>Secret code:</div>
                    <div className={cls.infoValue}>
                      <TextInput
                        name={'manager.secretCode'}
                        source={'manager.secretCode'}
                        label={false}
                        inputProps={{ maxLength: 6, minLength: 6 }}
                        onInput={transformDigitInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {manager && manager.id && <IndividualManagers manager={manager} />}
      </Container>
    </SimpleForm>
  );
};

const transformTrimInput = (e: any) => {
  if (e.target && e.target.value) {
    e.target.value = (e.target.value || '').replace(/\s\s+/g, ' ').replace(/^\s+/g, '');
  }
};

const transformDigitInput = (e: any) => {
  if (!e.target) return;
  e.target.value = String(e.target.value ?? '').replace(/\D+/g, '');
};

const transformNumberInput = (e: any) => {
  if (e.target && e.target.value) {
    if (e.target.value > 999999999) {
      e.target.value = 999999999;
    }
    if (e.target.value < 0) {
      e.target.value = 0;
    }
  } else {
    e.target.value = 0;
  }
};
